import axios from "axios";
import { backendRequestHeaders } from "./backendApiHeaders";
import { getApiBaseUrl } from "./apiUrl";
import { installBackendAxiosAuth } from "./installBackendAxiosAuth";

if (typeof window !== "undefined") {
  installBackendAxiosAuth();
}

function apiClient(token) {
  const headers = backendRequestHeaders();
  if (token?.trim() && !headers.Authorization) {
    headers.Authorization = `Bearer ${token.trim()}`;
  }
  return axios.create({ headers, timeout: 20000 });
}

export async function fetchBookableStaff({ sellerId, productId }) {
  const params = new URLSearchParams({ sellerId, productId });
  const { data } = await apiClient().get(
    `${getApiBaseUrl()}/services/staff?${params.toString()}`
  );
  return data;
}

export async function fetchServiceSlots({
  sellerId,
  productId,
  staffId,
  from,
  to,
  addOnIds = [],
}) {
  const params = new URLSearchParams({ sellerId, productId });
  if (staffId) params.set("staffId", staffId);
  if (from) params.set("from", from);
  if (to) params.set("to", to);
  if (addOnIds?.length) params.set("addOnIds", addOnIds.map(String).join(","));
  const { data } = await apiClient().get(
    `${getApiBaseUrl()}/services/slots?${params.toString()}`
  );
  return data;
}

export async function holdServiceBooking({ productId, startsAt, staffId, notes, token }) {
  const { data } = await apiClient(token).post(
    `${getApiBaseUrl()}/services/bookings/hold`,
    { productId, startsAt, staffId, notes }
  );
  return data;
}

/** Normalize GET /manage — backend returns `{ staff: [] }`; tolerate a bare array. */
export function normalizeStaffManageResponse(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.staff)) return data.staff;
  return [];
}

export function isStaffActiveForBooking(member) {
  if (!member) return false;
  if (member.active === false || member.active === "false") return false;
  return true;
}

export async function fetchSellerStaffManage(sellerId, token) {
  const { data } = await apiClient(token).get(
    `${getApiBaseUrl()}/services/staff/seller/${sellerId}/manage`
  );
  return { staff: normalizeStaffManageResponse(data) };
}

export async function createServiceStaff(sellerId, payload, token) {
  const { data } = await apiClient(token).post(
    `${getApiBaseUrl()}/services/staff/seller/${sellerId}`,
    payload
  );
  return data;
}

export async function updateServiceStaff(sellerId, staffId, payload, token) {
  const { data } = await apiClient(token).put(
    `${getApiBaseUrl()}/services/staff/seller/${sellerId}/${staffId}`,
    payload
  );
  return data;
}

export async function deleteServiceStaff(sellerId, staffId, token) {
  const { data } = await apiClient(token).delete(
    `${getApiBaseUrl()}/services/staff/seller/${sellerId}/${staffId}`
  );
  return data;
}

export async function fetchBookingByRef(ref) {
  const { data } = await apiClient().get(
    `${getApiBaseUrl()}/services/bookings/ref/${ref}`
  );
  return data;
}

/** Cancel a cart hold so the time slot becomes bookable again. */
export async function releaseServiceBookingHold(ref, token) {
  const { data } = await apiClient(token).post(
    `${getApiBaseUrl()}/services/bookings/ref/${encodeURIComponent(ref)}/release`,
    { reason: "Removed from cart" }
  );
  return data;
}

export async function fetchSellerAvailability(sellerId, token) {
  const { data } = await apiClient(token).get(
    `${getApiBaseUrl()}/services/availability/${sellerId}`
  );
  return data;
}

export async function updateSellerAvailability(sellerId, payload, token) {
  const { data } = await apiClient(token).put(
    `${getApiBaseUrl()}/services/availability/${sellerId}`,
    payload
  );
  return data;
}

export async function fetchSellerBookings(sellerId, { status, page, limit } = {}, token) {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (page) params.set("page", String(page));
  if (limit) params.set("limit", String(limit));
  const qs = params.toString();
  const { data } = await apiClient(token).get(
    `${getApiBaseUrl()}/services/bookings/seller/${sellerId}${qs ? `?${qs}` : ""}`
  );
  return data;
}

export async function updateBookingStatus(bookingId, status, token, cancellationReason) {
  const { data } = await apiClient(token).put(
    `${getApiBaseUrl()}/services/bookings/${bookingId}/status`,
    { status, cancellationReason }
  );
  return data;
}
